/**
 * WhatsApp quote follow-up automation — read state and Ops controls on quote detail.
 */

import React, { useCallback, useEffect, useMemo, useState } from "react";
import {
  Alert,
  Box,
  Button,
  Card,
  CardContent,
  Chip,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  TextField,
  Typography,
} from "@mui/material";
import { format, parseISO } from "date-fns";
import {
  getQuoteFollowUpAutomation,
  pauseQuoteFollowUpAutomation,
  resumeQuoteFollowUpAutomation,
  cancelQuoteFollowUpAutomation,
  rescheduleNextQuoteFollowUp,
} from "../services/quoteFollowUpAutomationService.js";
import { formatAutomationStepDetail } from "../utils/automationStepDetail.js";

function formatWhen(value) {
  if (!value) return "—";
  try {
    const d = typeof value === "string" ? parseISO(value) : new Date(value);
    if (Number.isNaN(d.getTime())) return String(value);
    return format(d, "dd MMM yyyy, HH:mm");
  } catch {
    return String(value);
  }
}

function stateChipColor(state) {
  switch ((state || "").toLowerCase()) {
    case "scheduled":
      return "info";
    case "active":
      return "primary";
    case "paused":
      return "warning";
    case "stopped":
      return "default";
    case "completed":
      return "success";
    default:
      return "default";
  }
}

function stepChipColor(status) {
  switch ((status || "").toLowerCase()) {
    case "scheduled":
      return "info";
    case "sent":
      return "success";
    case "skipped":
    case "cancelled":
      return "default";
    case "failed":
      return "error";
    default:
      return "default";
  }
}

function humanizeStopReason(reason) {
  if (!reason) return "";
  return String(reason)
    .replace(/_/g, " ")
    .replace(/\b\w/g, (c) => c.toUpperCase());
}

export default function QuoteFollowUpAutomationCard({
  quoteId,
  quoteSentAt,
  canManage = false,
}) {
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [automation, setAutomation] = useState(null);
  const [notFound, setNotFound] = useState(false);
  const [actionBusy, setActionBusy] = useState(false);
  const [cancelOpen, setCancelOpen] = useState(false);
  const [rescheduleOpen, setRescheduleOpen] = useState(false);
  const [rescheduleIso, setRescheduleIso] = useState("");

  const load = useCallback(async () => {
    if (!quoteId) return;
    setLoading(true);
    setError(null);
    const result = await getQuoteFollowUpAutomation(quoteId);
    if (!result.success) {
      setError(result.error || "Could not load automation");
      setAutomation(null);
      setNotFound(false);
    } else if (result.notFound || !result.data) {
      setAutomation(null);
      setNotFound(true);
    } else {
      setAutomation(result.data);
      setNotFound(false);
    }
    setLoading(false);
  }, [quoteId]);

  useEffect(() => {
    load();
  }, [load, quoteSentAt]);

  const terminal = useMemo(() => {
    const s = (automation?.state || "").toLowerCase();
    return s === "stopped" || s === "completed";
  }, [automation?.state]);

  const pausable = useMemo(() => {
    const s = (automation?.state || "").toLowerCase();
    return s === "scheduled" || s === "active";
  }, [automation?.state]);

  const resumable = useMemo(() => {
    return (automation?.state || "").toLowerCase() === "paused";
  }, [automation?.state]);

  const runAction = async (fn) => {
    setActionBusy(true);
    try {
      const r = await fn();
      if (r.success && r.data) {
        setAutomation(r.data);
        setError(null);
      } else {
        setError(r.error || "Action failed");
      }
    } finally {
      setActionBusy(false);
    }
  };

  const openReschedule = () => {
    if (automation?.nextScheduledAt) {
      try {
        const d = parseISO(automation.nextScheduledAt);
        if (!Number.isNaN(d.getTime())) {
          setRescheduleIso(format(d, "yyyy-MM-dd'T'HH:mm"));
        }
      } catch {
        setRescheduleIso("");
      }
    } else {
      setRescheduleIso("");
    }
    setRescheduleOpen(true);
  };

  if (!quoteId) {
    return null;
  }

  return (
    <Card
      elevation={1}
      sx={{
        mb: 3,
        borderRadius: { xs: 3, sm: 2 },
        overflow: "hidden",
      }}
    >
      <CardContent sx={{ p: { xs: 2, sm: 3 } }}>
        <Box
          sx={{
            display: "flex",
            justifyContent: "space-between",
            alignItems: { xs: "stretch", sm: "flex-start" },
            flexDirection: { xs: "column", sm: "row" },
            gap: 2,
            mb: 2,
          }}
        >
          <Box sx={{ minWidth: 0 }}>
            <Typography
              variant="h6"
              gutterBottom
              sx={{ fontWeight: 700, fontSize: { xs: "1.1rem", sm: "1.25rem" } }}
            >
              WhatsApp follow-up automation
            </Typography>
            <Typography variant="body2" color="text.secondary">
              Scheduled messages after the quote is sent. Stopped when the
              customer replies (via Ops or n8n) or when you cancel manually.
            </Typography>
          </Box>
          <Button
            size="small"
            variant="outlined"
            onClick={load}
            disabled={loading}
            sx={{ alignSelf: { xs: "flex-start", sm: "auto" } }}
          >
            Refresh
          </Button>
        </Box>

        {loading && (
          <Box sx={{ py: 2, display: "flex", justifyContent: "center" }}>
            <CircularProgress size={32} />
          </Box>
        )}

        {!loading && error && (
          <Alert severity="error" sx={{ mb: 2 }}>
            {error}
          </Alert>
        )}

        {!loading && !error && notFound && (
          <Alert severity="info">
            No automation sequence for this quote yet. Mark the quote as sent or
            send/share it — a sequence is created on first send when supported.
          </Alert>
        )}

        {!loading && automation && (
          <>
            <Box
              sx={{
                display: "flex",
                flexWrap: "wrap",
                gap: 1,
                mb: 2,
                "& .MuiChip-root": {
                  maxWidth: "100%",
                },
              }}
            >
              <Chip
                label={automation.state || "unknown"}
                color={stateChipColor(automation.state)}
                size="small"
                variant="outlined"
              />
              {automation.stopReason && (
                <Chip
                  label={`Stop: ${humanizeStopReason(automation.stopReason)}`}
                  size="small"
                  variant="outlined"
                />
              )}
              <Chip
                label={`Next step: ${automation.nextStep || "—"}`}
                size="small"
                variant="outlined"
              />
              <Chip
                label={`Scheduled: ${formatWhen(automation.nextScheduledAt)}`}
                size="small"
                variant="outlined"
              />
            </Box>

            {(automation.stopDetails?.message ||
              automation.stopDetails?.intent) && (
              <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                {automation.stopDetails?.intent &&
                  `Intent: ${automation.stopDetails.intent}`}
                {automation.stopDetails?.message &&
                  ` — ${automation.stopDetails.message}`}
              </Typography>
            )}

            {canManage && !terminal && (
              <Box sx={{ display: "flex", flexWrap: "wrap", gap: 1, mb: 2 }}>
                <Button
                  size="small"
                  variant="outlined"
                  disabled={!pausable || actionBusy}
                  onClick={() =>
                    runAction(() => pauseQuoteFollowUpAutomation(quoteId))
                  }
                >
                  Pause
                </Button>
                <Button
                  size="small"
                  variant="outlined"
                  disabled={!resumable || actionBusy}
                  onClick={() =>
                    runAction(() => resumeQuoteFollowUpAutomation(quoteId))
                  }
                >
                  Resume
                </Button>
                <Button
                  size="small"
                  variant="outlined"
                  disabled={
                    actionBusy || terminal || !automation?.nextStep
                  }
                  onClick={openReschedule}
                >
                  Reschedule next step
                </Button>
                <Button
                  size="small"
                  color="warning"
                  variant="contained"
                  disabled={actionBusy}
                  onClick={() => setCancelOpen(true)}
                >
                  Cancel sequence
                </Button>
              </Box>
            )}

            <Typography variant="subtitle2" sx={{ mb: 1, fontWeight: 700 }}>
              Steps
            </Typography>
            <Box sx={{ display: { xs: "grid", sm: "none" }, gap: 1 }}>
              {(automation.steps || []).map((step) => {
                const detail = formatAutomationStepDetail(step);
                return (
                  <Box
                    key={step.key}
                    sx={{
                      border: "1px solid",
                      borderColor: "divider",
                      borderRadius: 2,
                      p: 1.5,
                      bgcolor: "background.paper",
                    }}
                  >
                    <Box
                      sx={{
                        display: "flex",
                        alignItems: "flex-start",
                        justifyContent: "space-between",
                        gap: 1,
                      }}
                    >
                      <Typography variant="body2" sx={{ fontWeight: 700 }}>
                        {step.label}
                      </Typography>
                      <Chip
                        label={step.status || "—"}
                        size="small"
                        color={stepChipColor(step.status)}
                        variant="outlined"
                      />
                    </Box>
                    <Typography
                      variant="caption"
                      color="text.secondary"
                      component="div"
                      sx={{ mt: 0.75 }}
                    >
                      Scheduled: {formatWhen(step.scheduledFor)}
                    </Typography>
                    {detail && (
                      <Typography
                        variant="caption"
                        color="text.secondary"
                        component="div"
                        sx={{ mt: 0.75, whiteSpace: "pre-wrap", wordBreak: "break-word" }}
                      >
                        {detail}
                      </Typography>
                    )}
                  </Box>
                );
              })}
            </Box>

            <Box sx={{ display: { xs: "none", sm: "block" }, overflowX: "auto" }}>
              <Table size="small">
                <TableHead>
                  <TableRow>
                    <TableCell>Step</TableCell>
                    <TableCell>Status</TableCell>
                    <TableCell>Scheduled for</TableCell>
                    <TableCell sx={{ minWidth: 200 }}>Error / detail</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {(automation.steps || []).map((step) => (
                    <TableRow key={step.key}>
                      <TableCell>{step.label}</TableCell>
                      <TableCell>
                        <Chip
                          label={step.status || "—"}
                          size="small"
                          color={stepChipColor(step.status)}
                          variant="outlined"
                        />
                      </TableCell>
                      <TableCell>{formatWhen(step.scheduledFor)}</TableCell>
                      <TableCell>
                        <Typography
                          variant="caption"
                          color="text.secondary"
                          component="div"
                          sx={{ whiteSpace: "pre-wrap", wordBreak: "break-word" }}
                        >
                          {formatAutomationStepDetail(step) || "—"}
                        </Typography>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </Box>

            {(automation.auditEvents || []).length > 0 && (
              <>
                <Typography variant="subtitle2" sx={{ mt: 2, mb: 1, fontWeight: 700 }}>
                  Recent audit
                </Typography>
                <Box
                  sx={{
                    maxHeight: 200,
                    overflow: "auto",
                    bgcolor: "action.hover",
                    borderRadius: 2,
                    p: 1,
                  }}
                >
                  {[...(automation.auditEvents || [])]
                    .reverse()
                    .slice(0, 12)
                    .map((ev, idx) => (
                      <Typography
                        key={`${ev.occurredAt}-${idx}`}
                        variant="caption"
                        component="div"
                        color="text.secondary"
                        sx={{
                          display: "block",
                          py: 0.75,
                          px: 1,
                          borderRadius: 1,
                          bgcolor: "background.paper",
                          "& + &": {
                            mt: 0.75,
                          },
                        }}
                      >
                        {formatWhen(ev.occurredAt)} — {ev.type}:{" "}
                        {ev.message || ""}
                        {ev.metadata?.error
                          ? ` — ${ev.metadata.error}`
                          : ""}
                        {ev.actor ? ` (${ev.actor})` : ""}
                      </Typography>
                    ))}
                </Box>
              </>
            )}
          </>
        )}
      </CardContent>

      <Dialog open={cancelOpen} onClose={() => !actionBusy && setCancelOpen(false)}>
        <DialogTitle>Cancel WhatsApp follow-up sequence?</DialogTitle>
        <DialogContent>
          <Typography variant="body2">
            Pending automated messages will not be sent for this quote. Manual
            follow-ups in Ops are unaffected.
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setCancelOpen(false)} disabled={actionBusy}>
            Back
          </Button>
          <Button
            color="warning"
            variant="contained"
            disabled={actionBusy}
            onClick={async () => {
              setActionBusy(true);
              setError(null);
              try {
                const r = await cancelQuoteFollowUpAutomation(quoteId, {});
                if (r.success && r.data) {
                  setAutomation(r.data);
                  setCancelOpen(false);
                } else {
                  setError(r.error || "Cancel failed");
                }
              } finally {
                setActionBusy(false);
              }
            }}
          >
            Cancel sequence
          </Button>
        </DialogActions>
      </Dialog>

      <Dialog open={rescheduleOpen} onClose={() => !actionBusy && setRescheduleOpen(false)}>
        <DialogTitle>Reschedule next automated step</DialogTitle>
        <DialogContent>
          <Typography variant="body2" sx={{ mb: 2 }}>
            Applies to the upcoming step in this sequence (not WhatsApp sends
            already delivered).
          </Typography>
          <TextField
            fullWidth
            type="datetime-local"
            label="Send next step at"
            InputLabelProps={{ shrink: true }}
            value={rescheduleIso}
            onChange={(e) => setRescheduleIso(e.target.value)}
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setRescheduleOpen(false)} disabled={actionBusy}>
            Close
          </Button>
          <Button
            variant="contained"
            disabled={actionBusy || !rescheduleIso}
            onClick={async () => {
              setActionBusy(true);
              setError(null);
              try {
                const isoOut = rescheduleIso
                  ? new Date(rescheduleIso).toISOString()
                  : "";
                const r = await rescheduleNextQuoteFollowUp(quoteId, isoOut);
                if (r.success && r.data) {
                  setAutomation(r.data);
                  setRescheduleOpen(false);
                } else {
                  setError(r.error || "Reschedule failed");
                }
              } finally {
                setActionBusy(false);
              }
            }}
          >
            Reschedule
          </Button>
        </DialogActions>
      </Dialog>
    </Card>
  );
}
